import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import { motion } from 'framer-motion';
import {
  Assessment,
  FilterList,
  ClearAll,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import 'dayjs/locale/it';
import PageHeader from '../components/ui/PageHeader';
import OverviewMetrics from '../components/ui/OverviewMetrics';
import { useSearchParams } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { productionService, Nesting } from '../services/productionService';
import socketService from '../services/socket';
import NestingCard from '../components/NestingCard';

const JobsPage: React.FC = () => {
  const { state } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const [nestings, setNestings] = useState<Nesting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMachineId, setSelectedMachineId] = useState<string>(() => searchParams.get('machineId') || 'all');
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(null);
  const [dateTo, setDateTo] = useState<Dayjs | null>(null);

  useEffect(() => { setSelectedMachineId(searchParams.get('machineId') || 'all'); }, [searchParams]);

  const loadNestings = useCallback(async (
    machineId?: string,
    from?: Dayjs | null,
    to?: Dayjs | null,
    options?: { silent?: boolean }
  ) => {
    const silent = options?.silent === true;
    if (!silent) {
      setLoading(true);
    }
    setError(null);
    try {
      const params: Record<string, string> = {};
      if (machineId && machineId !== 'all') params.machineId = machineId;
      if (from?.isValid()) params.startDate = from.startOf('day').toISOString();
      if (to?.isValid()) params.endDate = to.endOf('day').toISOString();
      const { nestings: data } = await productionService.getNestings(
        Object.keys(params).length ? params : undefined
      );
      setNestings(data);
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || 'Errore caricamento lavorazioni');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const machineId = selectedMachineId === 'all' ? undefined : selectedMachineId;
    loadNestings(machineId, dateFrom, dateTo);
  }, [selectedMachineId, dateFrom, dateTo, loadNestings]);

  const handleClearFilters = () => {
    setSelectedMachineId('all');
    setSearchParams({}, { replace: true });
    setDateFrom(null);
    setDateTo(null);
  };

  const hasActiveFilters = selectedMachineId !== 'all' || dateFrom !== null || dateTo !== null;

  // Real-time updates via WebSocket
  useEffect(() => {
    socketService.connect();
    const machineIdsToJoin = selectedMachineId === 'all'
      ? (state.machines || []).map((machine) => machine.id)
      : [selectedMachineId];
    socketService.joinMachines(machineIdsToJoin);

    const handleProductionUpdate = (data: any) => {
      console.log('[JobsPage] Production update:', data.type);

      if (data.type === 'nesting_start') {
        // Check if nesting belongs to selected machine
        if (selectedMachineId === 'all' || data.nesting.machineId === selectedMachineId) {
          setNestings(prev => [data.nesting, ...prev]);
        }
      } else if (data.type === 'nesting_complete') {
        setNestings(prev => prev.map(n =>
          n.id === data.nesting.id ? { ...n, ...data.nesting } : n
        ));
      } else if (data.type === 'part_start' || data.type === 'part_complete' ||
                 data.type === 'path_start' || data.type === 'path_complete') {
        // Reload to get updated nested data
        const machineId = selectedMachineId === 'all' ? undefined : selectedMachineId;
        loadNestings(machineId, dateFrom, dateTo, { silent: true });
      }
    };

    socketService.on('production_update', handleProductionUpdate);

    return () => {
      socketService.off('production_update', handleProductionUpdate);
    };
  }, [selectedMachineId, dateFrom, dateTo, state.machines, loadNestings]);

  // Calculate stats
  const inProgressCount = nestings.filter(n => n.status === 'IN_PROGRESS').length;
  const completedCount = nestings.filter(n => n.status === 'COMPLETED').length;
  const cancelledCount = nestings.filter(n => n.status === 'CANCELLED').length;
  const errorCount = nestings.filter(n => n.status === 'ERROR').length;

  // Machines to show in the filter (include offline so admin can filter historical jobs)
  const machinesForFilter = state.machines || [];

  const buildMachineFilterLabel = (machine: any): string => {
    const customer = machine.companyName || 'Non assegnato';
    const reseller = machine.resellerName || 'Diretto';
    const serial = machine.serialNumber || '-';
    return `${machine.name} • SN: ${serial} • Cliente: ${customer} • Reseller: ${reseller}`;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
    <Box>
      {/* Header Section */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Box sx={{ mb: 3 }}>
          <PageHeader title="Lavorazioni" subtitle="Monitora le lavorazioni in corso e consulta lo storico per macchina e periodo." />
          {/* Filters Row */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              {/* Machine Filter */}
              {machinesForFilter.length > 0 && (
                <FormControl sx={{ minWidth: 250 }}>
                  <InputLabel id="jobs-machine-label">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FilterList fontSize="small" />
                      Filtra per Macchina
                    </Box>
                  </InputLabel>
                  <Select
                    labelId="jobs-machine-label"
                    value={selectedMachineId}
                    onChange={(e) => { setSelectedMachineId(e.target.value); setSearchParams(e.target.value === 'all' ? {} : { machineId: e.target.value }, { replace: true }); }}
                    label="Filtra per Macchina"
                  >
                    <MenuItem value="all">
                      <em>Tutte le macchine</em>
                    </MenuItem>
                    {machinesForFilter.map(machine => (
                      <MenuItem key={machine.id} value={machine.id}>
                        {buildMachineFilterLabel(machine)} {machine.status === 'offline' ? '(offline)' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {/* Date From */}
              <DatePicker
                label="Dal"
                value={dateFrom}
                onChange={(val) => setDateFrom(val)}
                maxDate={dateTo ?? undefined}
                slotProps={{
                  textField: { size: 'small', sx: { minWidth: 160 } },
                  field: { clearable: true },
                }}
              />

              {/* Date To */}
              <DatePicker
                label="Al"
                value={dateTo}
                onChange={(val) => setDateTo(val)}
                minDate={dateFrom ?? undefined}
                slotProps={{
                  textField: { size: 'small', sx: { minWidth: 160 } },
                  field: { clearable: true },
                }}
              />

              {/* Clear Filters */}
              {hasActiveFilters && (
                <Button
                  variant="outlined"
                  color="inherit"
                  startIcon={<ClearAll />}
                  onClick={handleClearFilters}
                  sx={{ height: 40, whiteSpace: 'nowrap' }}
                >
                  Rimuovi filtri
                </Button>
              )}
            </Box>
          </motion.div>
        </Box>
      </motion.div>

      <Box sx={{ mb: 3 }}><OverviewMetrics items={[
        { label: 'In produzione', value: loading ? '—' : inProgressCount, detail: 'Lavorazioni in corso', color: 'primary.main' },
        { label: 'Completate', value: loading ? '—' : completedCount, detail: 'Nel risultato selezionato', color: 'success.main' },
        { label: 'Annullate', value: loading ? '—' : cancelledCount, detail: 'Interrotte prima del termine' },
        { label: 'Con errori', value: loading ? '—' : errorCount, detail: 'Da verificare', color: 'error.main' },
      ]} /></Box>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && !loading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        </motion.div>
      )}

      {/* Nestings Grid */}
      {!loading && !error && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.6 }}
        >
          <Typography
            variant="h6"
            component="h2"
            gutterBottom
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 3
            }}
          >
            Elenco lavorazioni
            <Assessment sx={{ color: 'primary.main' }} />
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {nestings.map((nesting, index) => (
              <motion.div
                key={nesting.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  delay: Math.min(0.03 * index, 0.15),
                  duration: 0.15,
                  type: 'spring'
                }}
              >
                <NestingCard nesting={nesting} />
              </motion.div>
            ))}
          </Box>

          {nestings.length === 0 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.15 }}
            >
              <Paper
                sx={{
                  p: 6,
                  textAlign: 'center',
                  background: 'var(--bg-card)',
                  borderRadius: 4,
                  border: '1px dashed var(--border-light)',
                }}
              >
                <Typography
                  variant="h5"
                  color="text.secondary"
                  gutterBottom
                  sx={{ fontWeight: 600 }}
                >
                  Nessuna Lavorazione
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {hasActiveFilters
                    ? 'Nessuna lavorazione trovata per i filtri selezionati.'
                    : 'Non ci sono lavorazioni al momento.'}
                </Typography>
              </Paper>
            </motion.div>
          )}
        </motion.div>
      )}
    </Box>
    </LocalizationProvider>
  );
};

export default JobsPage;
